/**
 * AnimatedBanner Component
 *
 * An animated notification banner that slides in from the top of the page.
 * Features smooth animations, customizable content, and action buttons.
 *
 * @component
 * @example
 * ```tsx
 * <AnimatedBanner
 *   isVisible={true}
 *   text="New feature available!"
 *   actionText="Learn More"
 *   onAction={() => console.log('Action clicked')}
 *   onClose={() => console.log('Closed')}
 *   showCloseButton={true}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { Lightbulb, X } from 'lucide-react';
import { Button } from '../atoms/button';
import Icon from '../molecules/Icon';
import { AnimatedBannerProps } from '../../types/AnimatedBanner.types';
import { cn } from '../../lib/utils';

export const AnimatedBanner: React.FC<AnimatedBannerProps> = ({
  isVisible,
  onClose,
  onAction,
  icon = Lightbulb,
  text,
  subText,
  actionText,
  bgColor = 'bg-green-50',
  showCloseButton = false,
  variant = 'web',
  className,
  testId = 'animated-banner',
  style,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Start animation after render
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Remove from DOM after animation
      setTimeout(() => setShouldRender(false), 300);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div
      data-testid={testId}
      className={cn(
        'absolute left-[var(--sidebar-width)] right-2 rounded-2xl z-30 glass glass-glow overflow-hidden transition-all duration-200 ease-out',
        isAnimating ? 'h-18' : 'h-0',
        variant === 'web' ? 'top-[theme(spacing.header)]' : 'top-[theme(spacing.plugin-header)]',
        className
      )}
      style={style}
    >
      <div className={cn(bgColor, 'px-4 py-3')}>
        <div className="container p-0 mx-auto flex items-center justify-between">
          <div className="flex items-center gap-5">
            {icon && (
              <div className="flex-shrink-0 p-2 border rounded-lg border-success-200">
                <Icon icon={icon} size={14} strokeWidth={2} />
              </div>
            )}
            <p className="text-xs font-semibold text-gray-700" data-testid={`${testId}-text`}>
              {text} {' '}
            {subText &&
              <span className="text-xs font-normal text-gray-600" data-testid={`${testId}-subtext`}>{subText}</span>
            }
            </p>
          </div>

          <div className="flex items-center gap-2">
            {actionText && (<Button
              variant="ghost"
              size="sm"
              onClick={onAction}
              data-testid={`${testId}-action-button`}
            >
              {actionText}
            </Button>)}
            {onClose && showCloseButton && (
              <button
                onClick={onClose}
                className={cn(
                  'p-1 transition-colors',
                  bgColor.includes('success')
                    ? 'text-success-600 hover:text-success-800'
                    : 'text-green-600 hover:text-green-800'
                )}
                data-testid={`${testId}-close-button`}
                aria-label="Close banner"
              >
                <Icon icon={X} size={18} strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

AnimatedBanner.displayName = 'AnimatedBanner';

export default AnimatedBanner;
